import React, { useState } from "react";
import { Check, Plus, Tag, X } from "lucide-react";

function LabelsSection({ labels = [], loading = false, onAddLabel, onRemoveLabel }) {
  const [isAdding, setIsAdding] = useState(false);
  const [labelName, setLabelName] = useState("");
  const labelRecords = Array.isArray(labels) ? labels : [];

  const handleAddLabel = async () => {
    const value = labelName.trim();
    if (!value || loading) {
      return;
    }
    try {
      await onAddLabel?.(value);
      setLabelName("");
      setIsAdding(false);
    } catch (error) {
      console.error("Failed to add label.", error);
    }
  };

  const handleRemoveLabel = async (label) => {
    if (loading) {
      return;
    }
    try {
      await onRemoveLabel?.(label);
    } catch (error) {
      console.error("Failed to remove label.", error);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddLabel();
    }

    if (event.key === "Escape") {
      setLabelName("");
      setIsAdding(false);
    }
  };

  return (
    <div className="min-w-0">
      {/* Labels */}
      {labelRecords.length > 0 ? (
        <div className="max-h-[110px] overflow-y-auto pr-0.5">
          <div className="flex flex-wrap gap-1.5">
            {labelRecords.map((label, index) => {
              const labelId = label?.id ?? label?.value ?? index;

              const name =
                typeof label === "string"
                  ? label
                  : label?.name || label?.label || label?.value || "Label";

              return (
                <div
                  key={labelId}
                  className=" group flex min-w-0 max-w-full items-center gap-1 rounded-md border border-theme-100 bg-theme-50 px-2 py-1.5 transition-all duration-200 hover:border-theme-200 hover:bg-theme-50 hover:shadow-[0_2px_7px_rgba(61,155,210,0.07)] dark:border-dark-border dark:bg-dark-soft-blue"
                >
                  {/* Label dot */}
                  <span
                    className="
                    h-1.5 w-1.5 shrink-0 rounded-full
                    bg-primary/70
                    dark:bg-dark-primary
                  "
                  />

                  {/* Label name */}
                  <span
                    className="
                    min-w-0 max-w-[120px] truncate
                    text-[9px] font-semibold
                    text-primary
                    dark:text-dark-primary
                  "
                    title={name}
                  >
                    {name}
                  </span>

                  {/* Remove */}
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => handleRemoveLabel(label)}
                    title="Remove label"
                    className="flex h-4 w-4 shrink-0 items-center justify-centerrounded-mdtext-primary/50transition-all duration-150hover:bg-whitehover:text-red-500active:scale-90disabled:cursor-not-alloweddisabled:opacity-40dark:hover:bg-dark-card"
                  >
                    <X size={9} strokeWidth={2.3}/>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Empty state */
        <div className=" flex flex-col items-center justify-center rounded-xl border border-dashed border-border-light bg-app-bg px-3 py-4 dark:border-dark-border dark:bg-dark-soft-blue">
          <div className=" mb-2 flex h-7 w-7 items-center justify-center rounded-lg border border-theme-100 bg-theme-50 text-primary dark:border-dark-border dark:bg-dark-card dark:text-dark-primary">
            <Tag size={12} strokeWidth={1.8}/>
          </div>
          <p className="text-[9px] font-medium text-text-secondary dark:text-dark-text-secondary">
            No labels added
          </p>
          <p className="mt-0.5 text-[8px] text-text-muted dark:text-dark-text-muted">
            Add a label to organize this contact
          </p>
        </div>
      )}

      {/* Add Label */}
      {isAdding ? (
        <div className="mt-2.5 rounded-xl borderborder-theme-100bg-card-bgp-1.5shadow-[0_3px_10px_rgba(61,155,210,0.06)]dark:border-dark-borderdark:bg-dark-card">
          <div className="flex min-w-0 items-center gap-1.5">
            <div className=" flex min-w-0 flex-1 items-center rounded-lg border border-border-light bg-app-bg px-2 transition-colors focus-within:border-theme-200 focus-within:bg-card-bg dark:border-dark-border dark:bg-dark-soft-blue dark:focus-within:border-dark-primary/30">
              <input
                type="text"
                value={labelName}
                onChange={(event) =>
                  setLabelName(event.target.value)
                }
                onKeyDown={handleKeyDown}
                placeholder="Label name..."
                autoFocus
                disabled={loading}
                className=" h-7 min-w-0 flex-1 bg-transparent text-[9px] text-text-secondary outline-none placeholder:text-text-muted dark:text-dark-text-primary dark:placeholder:text-dark-text-muted"
              />
            </div>
            {/* Confirm */}
            <button
              type="button"
              disabled={
                loading || !labelName.trim()
              }
              onClick={handleAddLabel}
              title="Add label"
              className=" flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary text-white shadow-[0_2px_6px_rgba(61,155,210,0.14)] transition-all duration-200 hover:bg-primary-dark active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Check
                size={11}
                strokeWidth={2.5}
              />
            </button>

            {/* Cancel */}
            <button
              type="button"
              disabled={loading}
              onClick={() => {
                setLabelName("");
                setIsAdding(false);
              }}
              title="Cancel"
              className=" flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-border-light bg-card-bg text-text-muted transition-all duration-200 hover:border-red-100 hover:bg-red-50 hover:text-red-500 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 dark:border-dark-border dark:bg-dark-soft-blue"
            >
              <X
                size={11}
                strokeWidth={2}
              />
            </button>
          </div>
        </div>
      ) : (
        /* Add button */
        <button
          type="button"
          disabled={loading}
          onClick={() => setIsAdding(true)}
          className=" mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-theme-100 bg-theme-50/50 px-2 py-2 text-[9px] font-semibold text-primary transition-all duration-200 hover:border-theme-200 hover:bg-theme-50 hover:shadow-[0_2px_7px_rgba(61,155,210,0.06)] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-primary dark:hover:border-dark-primary/30"
        >
          <Plus
            size={11}
            strokeWidth={2.2}
          />
          <span>Add Label</span>
        </button>
      )}
    </div>
  );
}

export default LabelsSection;